import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, Marker, InfoWindow, Polygon } from '@react-google-maps/api';
import { API_URL, resolveUploadUrl } from '../config';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { FaMapMarkerAlt, FaSpinner, FaUsers, FaEnvelope, FaPhone, FaUserTie, FaArrowRight, FaMinus } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { operatingAreaMatches } from '../data/operatingAreas';

const MAP_STYLES = { height: '70vh', width: '100%' };
const ZA = { lat: -28.4793, lng: 24.6729 };

// Modern brand pins (SVG data URIs — green = main yard, blue = extra depot)
const PIN_MAIN = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="38" height="48" viewBox="0 0 38 48"><path d="M19 0C8.5 0 0 8.5 0 19c0 14.5 19 29 19 29s19-14.5 19-29C38 8.5 29.5 0 19 0z" fill="#00A86B"/><circle cx="19" cy="19" r="7.5" fill="#ffffff"/></svg>'
);
const PIN_EXTRA = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="0 0 38 48"><path d="M19 0C8.5 0 0 8.5 0 19c0 14.5 19 29 19 29s19-14.5 19-29C38 8.5 29.5 0 19 0z" fill="#3B82F6"/><circle cx="19" cy="19" r="7.5" fill="#ffffff"/></svg>'
);

function SubbieLocationsMap() {
  const { isLoaded } = useGoogleMaps();
  const [subbies, setSubbies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // marker object
  const [highlightPaths, setHighlightPaths] = useState([]); // coverage polygons (lat/lng rings)
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);

  const fetchSubbies = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/subcontractors`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (data.success) setSubbies(data.subcontractors || []);
    } catch (e) {
      console.error('❌ Subbie locations fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSubbies(); }, [fetchSubbies]);

  // Lazy-cached boundary data for the coverage highlight.
  const areaDataRef = useRef(null);

  const loadBoundaryData = useCallback(async () => {
    if (areaDataRef.current) return areaDataRef.current;
    // NOTE: must be an absolute frontend-origin URL — the global fetch wrapper in
    // config.js rewrites relative '/...' paths into the API origin (as '/api/...'),
    // which would 404. The boundary files ship alongside the app itself.
    const base = window.location.origin;
    const [provRes, countryRes] = await Promise.all([
      fetch(`${base}/data/sa-provinces.geojson`),
      fetch(`${base}/data/world-countries.geojson`)
    ]);
    if (!provRes.ok || !countryRes.ok) throw new Error(`Boundary fetch failed (${provRes.status}/${countryRes.status})`);
    areaDataRef.current = {
      provinces: await provRes.json(),
      countries: await countryRes.json()
    };
    return areaDataRef.current;
  }, []);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
    setMapReady(true);
    // Preload boundary data so the highlight appears instantly on pin click.
    loadBoundaryData().catch((e) => console.warn('Boundary preload failed:', e));
  }, [loadBoundaryData]);
  const onUnmount = useCallback(() => { mapRef.current = null; setMapReady(false); }, []);

  // On first open, zoom out to show the whole of South Africa (plus any pins outside it).
  const fittedOnceRef = useRef(false);

  const clearHighlight = useCallback(() => setHighlightPaths([]), []);

  // Yellow, semi-transparent overlay of the provinces/countries this subbie covers.
  const highlightOperatingAreas = useCallback(async (subbie) => {
    setHighlightPaths([]);
    const areas = String(subbie.operating_areas || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (areas.length === 0) return;
    try {
      const data = await loadBoundaryData();
      const { provinces, countries } = operatingAreaMatches(areas);
      const features = [];
      data.provinces.features.forEach((f) => {
        if (provinces.has(String(f.properties.name || '').toLowerCase())) features.push(f);
      });
      data.countries.features.forEach((f) => {
        if (countries.has(String(f.properties.NAME || f.properties.name || '').toLowerCase())) features.push(f);
      });
      if (features.length === 0) {
        console.warn('Operating-areas: no boundary features matched', areas);
        return;
      }
      const paths = [];
      const bounds = new window.google.maps.LatLngBounds();
      const addRing = (ring) => {
        const path = ring.map(([lng, lat]) => ({ lat, lng }));
        if (path.length < 3) return;
        path.forEach((pt) => bounds.extend(pt));
        paths.push(path);
      };
      const pushRings = (geometry) => {
        if (!geometry) return;
        if (geometry.type === 'Polygon') geometry.coordinates.forEach(addRing);
        else if (geometry.type === 'MultiPolygon') geometry.coordinates.forEach((poly) => poly.forEach(addRing));
      };
      features.forEach((f) => pushRings(f.geometry));
      setHighlightPaths(paths);
      if (paths.length > 0 && mapRef.current && !bounds.isEmpty()) {
        mapRef.current.fitBounds(bounds);
      }
    } catch (e) {
      console.error('Operating-areas highlight error:', e);
    }
  }, [loadBoundaryData]);

  const markers = [];
  subbies.forEach((s) => {
    if (s.company_lat && s.company_lng) {
      markers.push({ subbie: s, lat: Number(s.company_lat), lng: Number(s.company_lng), label: s.company_name, address: s.company_address, isMain: true });
    }
    (Array.isArray(s.locations) ? s.locations : [])
      .filter((l) => l && l.lat && l.lng)
      .forEach((l) => {
        markers.push({ subbie: s, lat: Number(l.lat), lng: Number(l.lng), label: l.label || s.company_name, address: l.address, isMain: false });
      });
  });

  // On first open, zoom out to show the whole of South Africa (plus any pins outside it).
  // Must live AFTER `markers` is built — referencing it in the deps array earlier is a TDZ error.
  useEffect(() => {
    if (!mapReady || fittedOnceRef.current || markers.length === 0) return;
    fittedOnceRef.current = true;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: -34.9, lng: 16.3 }); // SW — Cape Agulhas
    bounds.extend({ lat: -22.0, lng: 33.1 }); // NE — Limpopo border
    markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    if (!bounds.isEmpty()) {
      try { mapRef.current.fitBounds(bounds, 40); } catch (e) { console.warn('fitBounds error:', e); }
    }
  }, [mapReady, markers]);

  const locatedCount = new Set(subbies.filter((s) => s.company_lat && s.company_lng).map((s) => s.id)).size;
  const sel = selected;

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center mb-3 gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#00A86B' }} />
            <span className="text-xs text-gray-600">Main yard</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#3B82F6' }} />
            <span className="text-xs text-gray-600">Extra depot</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FBBF24' }} />
            <span className="text-xs text-gray-600">Coverage (click a pin)</span>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-4 py-2">
          <FaUsers className="text-green-500" />
          <span className="text-xs font-medium text-gray-600">{locatedCount}/{subbies.length} subbies with a pin</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading || !isLoaded ? (
            <div className="flex items-center justify-center" style={MAP_STYLES}>
              <FaSpinner className="animate-spin text-green-500 text-3xl" />
            </div>
          ) : markers.length === 0 ? (
            <div className="flex flex-col items-center justify-center" style={MAP_STYLES}>
              <FaMapMarkerAlt className="text-4xl text-gray-300 mb-3" />
              <p className="text-gray-500">No subbie locations set yet.</p>
              <p className="text-sm text-gray-400">Subbies pin their address with the Google Maps picker on their profile.</p>
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={MAP_STYLES}
              center={ZA}
              zoom={5}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={() => { setSelected(null); clearHighlight(); }}
              options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: true }}
            >
              {highlightPaths.map((path, i) => (
                <Polygon
                  key={`hl-${i}`}
                  paths={path}
                  options={{
                    fillColor: '#FBBF24',
                    fillOpacity: 0.45,
                    strokeColor: '#D97706',
                    strokeWeight: 1.5,
                    clickable: false,
                    zIndex: 1
                  }}
                />
              ))}
              {markers.map((m, i) => (
                <Marker
                  key={`${m.subbie.id}-${i}`}
                  position={{ lat: m.lat, lng: m.lng }}
                  onClick={() => { setSelected(m); highlightOperatingAreas(m.subbie); }}
                  icon={{ url: m.isMain ? PIN_MAIN : PIN_EXTRA, scaledSize: { width: m.isMain ? 38 : 34, height: m.isMain ? 48 : 44 } }}
                  title={m.address || m.label}
                />
              ))}

              {sel && (
                <InfoWindow
                  position={{ lat: sel.lat, lng: sel.lng }}
                  onCloseClick={() => { setSelected(null); clearHighlight(); }}
                >
                  <div style={{ maxWidth: '280px' }}>
                    <div className="flex items-start gap-2">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {sel.subbie.company_logo_url ? (
                          <img
                            src={resolveUploadUrl(sel.subbie.company_logo_url)}
                            alt="logo"
                            className="w-10 h-10 rounded-lg object-contain border border-gray-100"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#0A1929' }}>
                            {(sel.subbie.company_name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-bold text-navy truncate" style={{ fontSize: '14px' }}>{sel.subbie.company_name}</p>
                          <p className="text-[10px] text-gray-500">{sel.isMain ? 'Main yard' : 'Extra depot'}{sel.label && sel.label !== sel.subbie.company_name ? ` · ${sel.label}` : ''}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelected(null)}
                        title="Minimize card — keep the coverage highlight visible. Click the pin to bring it back."
                        className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 transition-colors"
                      >
                        <FaMinus size={12} />
                      </button>
                    </div>

                    <div className="mt-2 space-y-1 text-xs text-gray-700">
                      {sel.subbie.contact_person && (
                        <p className="flex items-center gap-1.5"><FaUserTie className="text-green-500" /> {sel.subbie.contact_person}</p>
                      )}
                      {sel.subbie.contact_phone && (
                        <p className="flex items-center gap-1.5"><FaPhone className="text-green-500" /> {sel.subbie.contact_phone}</p>
                      )}
                      {sel.subbie.contact_email && (
                        <p className="flex items-center gap-1.5 break-all"><FaEnvelope className="text-green-500" /> {sel.subbie.contact_email}</p>
                      )}
                      {sel.address && (
                        <p className="flex items-start gap-1.5"><FaMapMarkerAlt className="text-green-500 mt-0.5 flex-shrink-0" /> {sel.address}</p>
                      )}
                    </div>

                    {String(sel.subbie.operating_areas || '').split(',').map((a) => a.trim()).filter(Boolean).length > 0 && (
                      <div className="mt-2">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Operating Areas</p>
                        <div className="flex flex-wrap gap-1">
                          {String(sel.subbie.operating_areas).split(',').map((a, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full text-[10px] bg-yellow-100 text-yellow-800 font-medium">
                              {a.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-2 flex items-center justify-between">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${sel.subbie.verification_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {sel.subbie.verification_status || 'pending'}
                      </span>
                      <Link to={`/subcontractors/${sel.subbie.id}`} className="text-xs text-blue-600 font-medium flex items-center gap-1">
                        View Profile <FaArrowRight size={10} />
                      </Link>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          )}
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 max-h-[70vh] overflow-y-auto">
          <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
            <FaUsers className="text-green-500" /> Subbies ({subbies.length})
          </h3>
          {subbies.length === 0 ? (
            <p className="text-sm text-gray-400">No subcontractors yet.</p>
          ) : (
            <div className="space-y-3">
              {subbies.map((s) => {
                const hasPin = !!(s.company_lat && s.company_lng);
                const extras = (Array.isArray(s.locations) ? s.locations : []).filter((l) => l && l.address);
                return (
                  <div key={s.id} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-sm text-navy">{s.company_name}</p>
                      <FaMapMarkerAlt className={`mt-0.5 flex-shrink-0 ${hasPin ? 'text-green-600' : 'text-gray-300'}`} />
                    </div>
                    {hasPin && s.company_address && (
                      <p className="text-xs text-gray-600 mt-1">📍 {s.company_address}</p>
                    )}
                    {extras.map((l, i) => (
                      <p key={i} className="text-xs text-gray-500 mt-0.5">
                        <span className="text-blue-500">🔵</span> {l.label ? `${l.label}: ` : ''}{l.address}
                      </p>
                    ))}
                    {!hasPin && extras.length === 0 && (
                      <p className="text-xs text-gray-400 mt-1">No location set yet</p>
                    )}
                    <Link to={`/subcontractors/${s.id}`} className="text-xs text-blue-600 hover:underline mt-1 inline-block">
                      View profile →
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubbieLocationsMap;
